import { isNumber } from 'lodash';
import { message, Modal } from 'ant-design-vue';
import moment from 'moment';
import dayjs from 'dayjs';
import { getApiData } from 'liyu-pc-base/api/api-model';
import workerCode from './worker.js?raw';
import { useWorkerHostFunctions } from 'liyu-pc-base/init-plugin';
import { defineAsyncComponent } from 'vue';
import { ConditionOperatorMap } from 'liyu-pc-base/api/typing';
import {
  registerPageAbility,
  type PageAbilityDefinition,
  type PageAbilityParameterSchema,
  type PageAbilityResult,
} from 'liyu-pc-base/components/page-ability/registry';
import {
  registerQuickAction,
  listQuickActions,
  extractQuickActionsFromOptions,
  QUICK_ACTION_REGISTRY_KEY,
  QUICK_ACTION_CHANGE_EVENT,
  type QuickActionButton,
} from '../components/quick-action/registry';

export type { PageAbilityDefinition, PageAbilityParameterSchema, PageAbilityResult };
export {
  registerQuickAction,
  listQuickActions,
  extractQuickActionsFromOptions,
  QUICK_ACTION_REGISTRY_KEY,
  QUICK_ACTION_CHANGE_EVENT,
};
export type { QuickActionButton };

export interface CustomPageAbilityRegistration<TParams = unknown>
  extends Omit<PageAbilityDefinition<TParams>, 'name'> {
  name: string;
  execute: (params: TParams) => Promise<PageAbilityResult> | PageAbilityResult;
}

/**
 * 注册个性化页面能力(带工具元信息)。
 *
 * 约定:
 * - `name` 为宿主页面真实能力名,供 page_ability_execute 调用
 * - `toolName` / `description` / `parameters` 等用于 page_ability_list 返回给模型
 * - 返回注销函数,组件卸载时调用
 */
export const registerCustomPageAbility = <TParams = unknown>(
  ability: CustomPageAbilityRegistration<TParams>,
): (() => void) =>
  registerPageAbility(ability.name, {
    ...ability,
  });
export const getTime = (time: any) => {
  if (time) {
    time = time.substring(0, 5);
  }
  return time;
};
export const getTime2 = (time: any) => {
  if (time) {
    time = time.substring(11, 16);
  }
  return time;
};
export const getTime3 = (time: any) => {
  if (time) {
    time = time.substring(11, 19);
  }
  return time;
};
export const getMonth = (time: any) => {
  if (time) {
    time = time.substring(0, 7);
  }
  return time;
};
export const getDateMonth = (time: any) => {
  if (time == '1900-01-01') {
    return '期初';
  } else if (time) {
    time = time.substring(0, 4) + '年' + time.substring(5, 7) + '月';
  }
  return time;
};

export const getDateYear = (time: any) => {
  if (time == '1900-01-01') {
    return '期初';
  } else if (time) {
    const date = dayjs(time);
    return date.isValid() ? `${date.format('YYYY')}年` : '';
  }
  return time;
};
export const getDate = (time: any) => {
  if (time && new Date().getFullYear().toString() == time.substring(0, 4)) {
    time = time.substring(5, 10);
  } else if (time) {
    time = time.substring(0, 10);
  }
  return time;
};
export const getFullDate = (time: any) => {
  if (time) {
    time = time.substring(0, 10);
  }
  return time;
};
export const getDateTime = (time: any) => {
  if (time && time.substring(0, 4) <= 1970) {
    return '';
  }
  if (time && new Date().getFullYear().toString() == time.substring(0, 4)) {
    time = time.substring(5, 16);
  } else if (time) {
    time = time.substring(0, 16);
  }
  return time;
};
export const getFullDateTime = (time: any) => {
  if (time && new Date().getFullYear().toString() == time.substring(0, 4)) {
    time = time.substring(5, 19);
  } else if (time) {
    time = time.substring(0, 19);
  }
  return time;
};
export const getFullYearDateTime = (time: any) => {
  if (time) {
    time = time.substring(0, 19);
  }
  return time;
};
export const toFixed2 = (num: any) => {
  if (isNumber(num)) {
    if (Number(num.toFixed(2)) == 0) {
      return 0;
    } else {
      return num.toFixed(2);
    }
  } else {
    return '';
  }
};
export const toFixed0 = (num: any) => {
  if (isNumber(num)) {
    if (Number(num.toFixed(0)) == 0) {
      return 0;
    } else {
      return num.toFixed(0);
    }
  } else {
    return '';
  }
};
export const toFixed3 = (num: any) => {
  if (isNumber(num)) {
    if (Number(num.toFixed(3)) == 0) {
      return 0;
    } else {
      return num.toFixed(3);
    }
  } else {
    return '';
  }
};
// 去掉字符串前后的空格
export const trim = (str: any) => {
  return str.replace(/(^\s*)|(\s*$)/g, '');
};
export const toFixed6 = (num: any) => {
  if (isNumber(num)) {
    if (Number(num.toFixed(6)) == 0) {
      return 0;
    } else {
      return num.toFixed(6);
    }
  } else {
    return '';
  }
};
// 转换时分秒
export const format_datetime_hh_mm = (second_time: any) => {
  let time: any = second_time;
  if (second_time > 60) {
    let min = second_time / 60;
    time = Math.floor(min) + '分';
    if (min > 60) {
      min = (second_time / 60) % 60;
      let hour = second_time / 60 / 60;
      time = Math.floor(hour) + '小时' + Math.floor(min) + '分';
      if (hour > 24) {
        hour = (second_time / 60 / 60) % 24;
        const day = second_time / 60 / 60 / 24;
        time = Math.floor(day) + '天' + Math.floor(hour) + '小时' + Math.floor(min) + '分';
      }
    }
  } else {
    time = second_time + '秒';
  }
  return time;
};

// 转换时分秒
export const format_ms_hh_mm = milliseconds => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  const remainingSeconds = seconds % 60;
  const remainingMinutes = minutes % 60;

  let formattedTime = '';

  if (hours > 0) {
    formattedTime += `${hours.toString().padStart(2, '0')}:`;
  }
  formattedTime += `${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;

  // 只添加毫秒部分如果毫秒数不为0
  if (milliseconds % 1000 !== 0) {
    formattedTime += `.${milliseconds.toString().slice(-2)}`;
  }
  return formattedTime;
};
// 字段对应错误————>转化正确
export const getObjList = (api_fields: any, record: any) => {
  const obj: any = {};
  for (const key in api_fields) {
    if (record[api_fields[key] as string]) {
      obj[key] = record[api_fields[key] as string];
    }
  }
  return obj;
};
export const getObjListBySelectedRowKeys = (
  api_fields: any,
  dataSource: any[],
  selectedRowKeys: any[],
) => {
  const flattenData = (items: any[]): any[] => {
    const result: any[] = [];
    for (const item of items) {
      result.push(item);
      if (item.children && item.children.length > 0) {
        result.push(...flattenData(item.children));
      }
    }
    return result;
  };

  const flatDataSource = flattenData(dataSource || []);
  const obj: any = {};
  for (const key in api_fields) {
    obj[key] = flatDataSource
      .filter(item => selectedRowKeys.includes(item.id))
      .map(item => item[api_fields[key] as string]);
  }
  return obj;
};
// 时间区间格式转化 数组-->字符串
export const getRangePicker = (api_fields: any, record: any) => {
  const obj: any = {};
  for (const key in api_fields) {
    if (record[api_fields[key] as string]) {
      obj[key] = record[api_fields[key] as string].join(',');
    }
  }
  return obj;
};

// 小数转整数--> 过钢量是小数，单位为吨。转化为整数。
export const getTon = (kgnum: number) => {
  return Math.floor(kgnum);
};

export const get_tag_color_by_slots = ({ column, text }) => {
  const color_list = [
    'green',
    'orange',
    'red',
    'pink',
    'cyan',
    'blue',
    'purple',
    '#f50',
    '#2db7f5',
    '#87d068',
    '#108ee9',
  ];
  const value_list = Object.keys(column)
    .filter((key: string) => key.indexOf('field_') == 0)
    .map((key: string) => key.replace('field_', ''))
    .sort();
  return color_list[value_list.indexOf(text.toString())];
};

export const get_remote_tree_data = (res: any) => {
  const treeData: any = [];
  const search_tree_remote = (item: any, list: []) => {
    list.map((item1: any) => {
      if (item.parent_id == item1.id) {
        if (item1.children == undefined) {
          item1.children = [];
        }
        item1.children.push({
          key: item.id,
          value: item.id,
          title: item.name,
          ...item,
          children: [],
        });
      } else {
        search_tree_remote(item, item1.children);
      }
    });
  };
  res.data.map((item: any) => {
    if (!item.parent_id) {
      treeData.push({
        key: item.id,
        value: item.id,
        title: item.name,
        ...item,
        children: [],
      });
    } else {
      search_tree_remote(item, treeData);
    }
  });
  return treeData;
};

export const get_page_tree_data = (res: any) => {
  const treeData: any = [];
  const search_tree = (item: any, list: []) => {
    list.map((item1: any) => {
      if (item.parent_id == item1.id) {
        if (!('children' in item1)) {
          item1.children = [];
        }
        item1.children.push({
          id: item.id,
          name: item.name,
          key: item.id,
        });
      } else {
        if (item1.children != undefined && item1.children.length > 0) {
          search_tree(item, item1.children);
        }
      }
    });
  };
  treeData.push({
    key: 999999,
    name: '全部',
    children: [],
  });

  res.data.map((item: any) => {
    if (!item.parent_id) {
      treeData.push({
        id: item.id,
        name: item.name,
        key: item.id,
        children: [],
      });
    } else {
      search_tree(item, treeData);
    }
  });
  treeData.map((item1: any) => {
    if (item1.children.length == 0) {
      delete item1.children;
    }
  });
  return treeData;
};
// 浮点数加减  相加：countSum( [ a, b, c ] )   相减：countSum( [ a, -b, -c ] ) 传参必须是 number
export const countSum: (arr: number[]) => number = arr => {
  if (!arr.length) return 0;
  arr = arr.map((v: number) => {
    if (v && !Number.isNaN(Number(v))) return Math.round(v * 10000);
    return 0;
  });
  const result = arr.reduce((prev: number, curr: number) => {
    return prev + curr;
  }, 0);
  return result / 10000;
};

export const copyValue = val => {
  let str = '';
  let messages = '复制成功';
  if (val?.message) {
    str = val.val;
    messages = val.message;
  } else {
    str = val;
  }
  const textarea = document.createElement('textarea');
  textarea.setAttribute('readonly', 'readonly');
  textarea.value = str;
  document.body.appendChild(textarea);
  textarea.select();
  if (document.execCommand('copy')) {
    message.info(messages);
  }
  document.body.removeChild(textarea);
};

export const filterNullAndEmptyKeys = record => {
  const obj: any = {};
  Object.keys(record).forEach(key => {
    if (record[key] !== null && record[key] !== '' && JSON.stringify(record[key]) !== '[]') {
      obj[key] = record[key];
    }
  });
  return obj;
};

export const isNotEmpty = value => {
  return value !== null && value !== undefined && value !== '';
};

export const showGroupFormat = (item, format) => {
  if (format == 'getPeriod') {
    item.group_info.display_name = getDateMonth(item.group_info.display_name);
  }
  return item;
};

export const customRenderMetaData = ({ record }) => {
  const obj = {
    props: {} as any,
  };
  if (record?.meta_data?.rowSpan != undefined) {
    obj.props.rowSpan = record.meta_data.rowSpan;
  }
  return obj;
};

export const isEmpty = value => {
  return value === null || value == undefined || value === '';
};

export const convertValueByClassName = (value, class_name) => {
  let new_value;
  if (class_name == 'int') {
    if (isNotEmpty(value) && value instanceof Array) {
      new_value = [];
      value.forEach(item => {
        if (isNotEmpty(item) && typeof item === 'string') {
          new_value.push(Number(item));
        } else {
          new_value.push(item);
        }
      });
    } else if (isNotEmpty(value) && typeof value === 'string') {
      new_value = Number(value);
    } else {
      new_value = value;
    }
  } else if (['bool', 'boolean'].includes(class_name)) {
    new_value = [];
    const true_list = ['true', 'True', 'TRUE', '1', 1, true];
    const false_list = ['false', 'False', 'FALSE', '0', 0, false];
    value.forEach(item => {
      if (true_list.includes(item)) {
        new_value.push(true);
      } else if (false_list.includes(item)) {
        new_value.push(false);
      } else {
        new_value.push(item);
      }
    });
  } else {
    new_value = value;
  }
  return new_value;
};

export const get_operator_type_map = (format, operator) => {
  const operator_type_map = ConditionOperatorMap;
  if (format == 'input' && operator == 'in') {
    return '等于';
  }
  return operator_type_map[operator];
};
export const nested_sort_list = (list: any) => {
  list.sort((a: any, b: any) => {
    return (a.order || 0) - (b.order || 0);
  });
  list.forEach((item: any) => {
    if (item.children) {
      nested_sort_list(item.children);
    }
  });
};

// 判断是否为有效日期
export const isValidDate = (dateString, format = 'YYYY-MM-DD') => {
  const date = moment(dateString, format, true);
  return date.isValid();
};

export const checkCondition = ({
  record,
  conditions,
  dataSource,
  selectedRowKeys,
}: {
  record?: Object;
  conditions: any;
  dataSource?: any[];
  selectedRowKeys?: any[];
}) => {
  // 如果条件组为空，直接返回true
  if (!conditions || conditions.length === 0) {
    return true;
  }

  const flattenData = (items: any[]): any[] => {
    const result: any[] = [];
    for (const it of items) {
      result.push(it);
      if (it.children && it.children.length > 0) {
        result.push(...flattenData(it.children));
      }
    }
    return result;
  };
  const flatDataSource = flattenData(dataSource || []);
  // 计算每个条件组的结果
  const groupResults = conditions.map(group => {
    // 如果组内没有条件，返回true
    if (!group || group.length === 0) return true;

    // 计算组内每个条件的结果
    const conditionResults = group.map(item => {
      if (!item) return false;
      let record_value = null;
      if (selectedRowKeys instanceof Array) {
        record_value = flatDataSource
          .filter(_item => selectedRowKeys.includes(_item.id))
          .map(_item => _item[item.flag]);
      } else {
        record_value = record[item.flag];
      }
      let newValue = convertValueByClassName(item.value, item.type);

      if (!(newValue instanceof Array)) {
        newValue = [newValue];
      }

      // 根据操作符判断条件
      switch (item.operator) {
        case 'lt':
          if (typeof newValue[0] === 'number') {
            return !isEmpty(record_value) && record_value < newValue[0];
          } else if (isValidDate(newValue[0])) {
            return !isEmpty(record_value) && moment(record_value).isBefore(moment(newValue[0]));
          }
          return false;

        case 'lte':
          if (typeof newValue[0] === 'number') {
            return !isEmpty(record_value) && record_value <= newValue[0];
          } else if (isValidDate(newValue[0])) {
            return (
              !isEmpty(record_value) &&
              (moment(record_value).isBefore(moment(newValue[0])) ||
                moment(record_value).isSame(moment(newValue[0])))
            );
          }
          return false;

        case 'gt':
          if (typeof newValue[0] === 'number') {
            return !isEmpty(record_value) && record_value > newValue[0];
          } else if (isValidDate(newValue[0])) {
            return !isEmpty(record_value) && moment(record_value).isAfter(moment(newValue[0]));
          }
          return false;

        case 'gte':
          if (typeof newValue[0] === 'number') {
            return !isEmpty(record_value) && record_value >= newValue[0];
          } else if (isValidDate(newValue[0])) {
            return (
              !isEmpty(record_value) &&
              (moment(record_value).isAfter(moment(newValue[0])) ||
                moment(record_value).isSame(moment(newValue[0])))
            );
          }
          return false;

        case 'between':
          if (typeof newValue[0] === 'number' && typeof newValue[1] === 'number') {
            return (
              !isEmpty(record_value) && record_value >= newValue[0] && record_value <= newValue[1]
            );
          } else if (isValidDate(newValue[0]) && isValidDate(newValue[1])) {
            return (
              !isEmpty(record_value) &&
              (moment(record_value).isAfter(moment(newValue[0])) ||
                moment(record_value).isSame(moment(newValue[0]))) &&
              (moment(record_value).isBefore(moment(newValue[1])) ||
                moment(record_value).isSame(moment(newValue[1])))
            );
          }
          return false;

        case 'notbetween':
          if (typeof newValue[0] === 'number' && typeof newValue[1] === 'number') {
            return (
              !isEmpty(record_value) && (record_value < newValue[0] || record_value > newValue[1])
            );
          } else if (isValidDate(newValue[0]) && isValidDate(newValue[1])) {
            return (
              !isEmpty(record_value) &&
              (moment(record_value).isBefore(moment(newValue[0])) ||
                moment(record_value).isAfter(moment(newValue[1])))
            );
          }
          return false;

        case 'in':
          if (record_value instanceof Array) {
            return newValue.some(x => record_value.includes(x));
          } else {
            return newValue.some(x => record_value == x);
          }
        case 'notin':
          if (record_value instanceof Array) {
            return !newValue.some(x => record_value.includes(x));
          } else {
            return !newValue.some(x => record_value == x);
          }
        case 'eq':
          return record_value === newValue[0];
        case 'not_eq':
          return record_value !== newValue[0];

        case 'contains':
          return record_value && record_value.toString().indexOf(newValue[0]) > -1;
        case 'icontains':
          return (
            record_value &&
            record_value.toString().toLowerCase().indexOf(newValue[0].toLowerCase()) > -1
          );

        case 'notcontains':
          return !record_value || record_value.toString().indexOf(newValue[0]) === -1;
        case 'noticontains':
          return (
            !record_value ||
            record_value.toString().toLowerCase().indexOf(newValue[0].toLowerCase()) === -1
          );

        case 'startswith':
          return record_value && record_value.toString().startsWith(newValue[0]);
        case 'istartswith':
          return (
            record_value &&
            record_value.toString().toLowerCase().startsWith(newValue[0].toLowerCase())
          );
        case 'notstartswith':
          return !record_value || record_value.toString().startsWith(newValue[0]);
        case 'notistartswith':
          return (
            !record_value ||
            record_value.toString().toLowerCase().startsWith(newValue[0].toLowerCase())
          );

        case 'json_list_not_blank_not_null':
        case 'not_blank_not_null':
          if (record_value instanceof Array) {
            return record_value.length !== 0;
          } else {
            return !!record_value;
          }
        case 'is_blank':
        case 'json_list_is_blank_is_null':
          if (record_value instanceof Array) {
            return record_value.length === 0;
          } else {
            return !record_value;
          }

        default:
          return false;
      }
    });

    // 组内条件是与逻辑，所有条件都要满足
    return conditionResults.every(result => result);
  });

  // 组间条件是或逻辑，只要有一个组满足条件即可
  return groupResults.some(result => result);
};
const confirm = ({ title, content }) => {
  return new Promise((resolve, reject) => {
    Modal.confirm({
      title: title,
      content: content,
      onOk() {
        resolve(true);
      },
      onCancel() {
        resolve(false);
      },
    });
  });
};
// 获取本周周日 日期
const getWeekday = day => {
  const date = moment(day, 'YYYYMMDD');
  // 计算本周周日（ISO周：周一+6天=周日）
  const sunday = date.clone().isoWeekday(7);
  return sunday.format('YYYY-MM-DD');
};
// 根据周日日期 周中日期，返回区间时间列表
const getWeekDays = ({ sundayDate, weekdayDate }) => {
  // 解析输入日期（支持YYYY-MM-DD和YYYYMMDD格式）
  const sunday = moment(sundayDate, 'YYYY-MM-DD');
  const targetDay = moment(weekdayDate, 'YYYY-MM-DD');
  // 生成日期序列
  const result = [];
  let currentDay = sunday.clone();
  // 从周日倒推到目标周中日期
  while (currentDay.isSameOrAfter(targetDay, 'day')) {
    result.push({
      weekday: currentDay.format('YYYY-MM-DD'),
      weekName: currentDay.format('dddd'), // 自动转换为中文星期名称
    });
    currentDay.subtract(1, 'days');
  }
  return result;
};
const isSameWeekWithDates = (dateStr1, dateStr2) => {
  // 解析并验证日期
  const date1 = new Date(dateStr1);
  const date2 = new Date(dateStr2);
  if (isNaN(date1) || isNaN(date2)) throw new Error('Invalid date format');

  // 计算周起始日（周一）
  const getMonday = date => {
    const temp = new Date(date);
    const dayOfWeek = temp.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    temp.setDate(temp.getDate() + diff);
    return temp;
  };

  // 判断是否同周
  const monday1 = getMonday(date1).getTime();
  const monday2 = getMonday(date2).getTime();
  const sameWeek = monday1 === monday2;

  // 生成日期数组
  const generateDateArray = (start, end) => {
    const dates = [];
    const current = new Date(start);
    const weekNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    while (current <= end) {
      dates.push({
        weekday: formatDate(current),
        weekName: weekNames[current.getDay()],
      });
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // 日期格式化函数
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // 返回结果
  if (!sameWeek) return { sameWeek: false };

  const start = new Date(Math.min(date1.getTime(), date2.getTime()));
  const end = new Date(Math.max(date1.getTime(), date2.getTime()));

  return {
    sameWeek: true,
    dates: generateDateArray(start, end),
  };
};
const GLOBAL_FUNC_MAP = {
  getApiData,
  confirm,
  getWeekday,
  getWeekDays,
  isSameWeekWithDates,
};

export const executeScriptInWorker = (
  code: string,
  params: any,
  hostFunctions: { [key: string]: (...args: any[]) => any } = {},
): Promise<any> => {
  const allFunctions = { ...GLOBAL_FUNC_MAP, ...hostFunctions, ...useWorkerHostFunctions() };
  return new Promise((resolve, reject) => {
    const workerScript = `${workerCode} 
    ${code}`;
    const blob = new Blob([workerScript], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));

    worker.onmessage = async event => {
      const { __type, id, name, args, success, data, error } = event.data;

      if (__type === 'call') {
        const func = allFunctions[name];
        if (func) {
          try {
            const result = await func(...args);
            worker.postMessage({ __type: 'result', id, result });
          } catch (e: any) {
            worker.postMessage({ __type: 'result', id, error: e.message });
          }
        } else {
          worker.postMessage({
            __type: 'result',
            id,
            error: `Host function '${name}' not found.`,
          });
        }
        return;
      }

      worker.terminate();
      if (success) {
        resolve(data);
      } else {
        reject(new Error(error));
      }
    };

    worker.onerror = error => {
      worker.terminate();
      reject(error);
    };
    worker.postMessage(JSON.parse(JSON.stringify(params)));
  });
};
export const filterConditions = conditions => {
  if (!conditions) {
    return [];
  }
  const res = [];
  for (const item of conditions) {
    if (Array.isArray(item)) {
      const new_item = filterConditions(item);
      if (new_item.length > 0) {
        res.push(new_item);
      }
    } else {
      if (
        item.value instanceof Array &&
        item.value.length > 0 &&
        !item.value.some(item => isEmpty(item))
      ) {
        res.push(item);
      }
    }
  }
  return res;
};

interface RemoteComponentOptions {
  url: string;
  libName: string;
  componentName: string;
  cssUrl?: string;
}

// 缓存已加载的模块，避免重复请求
const moduleCache = new Map<string, any>();
export const defineRemoteComponent = ({
  url,
  libName,
  componentName,
  cssUrl,
}: RemoteComponentOptions) => {
  return defineAsyncComponent({
    loader: async () => {
      // 1. 动态加载 CSS（如果需要）
      if (cssUrl && !document.querySelector(`link[href="${cssUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
        await new Promise(r => (link.onload = r));
      }

      // 2. 加载 JS 模块 (UMD)
      if (!moduleCache.has(libName)) {
        const script = document.createElement('script');
        script.src = url;
        document.head.appendChild(script);
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });
        moduleCache.set(libName, (window as any)[libName]);
      }

      const mod = moduleCache.get(libName);
      if (!mod) {
        throw new Error(`Failed to load module ${libName} from ${url}`);
      }

      // 3. 返回指定组件
      const component = mod[componentName];
      if (!component) {
        throw new Error(`Component "${componentName}" not found in global DynamicComponent`);
      }
      return component;
    },
    loadingComponent: {
      setup() {
        return () => '加载中...';
      },
    },
    errorComponent: {
      props: ['error', 'retry'],
      setup(props) {
        return () =>
          `加载失败: ${props.error.message} <button onclick="(${props.retry})()">重试</button>`;
      },
    },
    delay: 200,
    timeout: 10000,
  });
};
export const strToBase64 = str => {
  // 字符串 → Uint8Array（二进制）→ Base64
  const encoder = new TextEncoder();
  const uint8Array = encoder.encode(str);
  return btoa(String.fromCharCode(...uint8Array));
};
const get_url_org_id = () => {
  // https://host/static_file_kpi_12/excel_report/excel_report-report_record#8f391979-6e31-4024-8fb2-205f09e82ffb
  // 例如上面的 URL，取出 12 作为 org_id;
  const reg = /.*_(\d+)$/;
  const url = new URL(window.location.href);
  const pathname = url.pathname;
  const pathargs = pathname.split('/');
  if (pathargs.length > 1) {
    const pre = pathargs[1];
    const org_id = pre.match(reg)?.[1] || null;
    return org_id;
  } else {
    return null;
  }
};
let current_org_id = null;
export const set_org_id = (org_id: any) => {
  const url_org_id = get_url_org_id();
  if (!url_org_id) {
    current_org_id = org_id;
    if (!org_id) {
      sessionStorage.removeItem('org_id');
      localStorage.removeItem('org_id');
    } else {
      sessionStorage.setItem('org_id', org_id.toString());
      localStorage.setItem('org_id', org_id.toString());
    }
  } else {
    sessionStorage.removeItem('org_id');
    localStorage.removeItem('org_id');
  }
};
export const get_org_id = (): number => {
  const url_org_id = get_url_org_id();
  if (url_org_id) {
    return Number(url_org_id);
  }
  if (!current_org_id) {
    // 优先从 sessionStorage 中获取，可保证不同 tab 页的 org_id 独立存储
    const stor_org_id = sessionStorage.getItem('org_id') || localStorage.getItem('org_id');
    if (stor_org_id) {
      current_org_id = Number(stor_org_id);
    }
  }
  return current_org_id;
};

export const genID = (): string => {
  const array = new Uint8Array(16); // 16 字节 = 128 位
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};
